import { Link, useLocation } from "react-router-dom"
import { useWordPressAuth } from "@/components/wordpress-auth-provider"
import { UserAvatar } from "@/components/user-avatar"
import { useTheme } from "@/components/theme-provider"
import { 
  LayoutDashboard, 
  Clock, 
  Settings, 
  LogOut, 
  Sun, 
  Moon, 
  FolderIcon, 
  CheckSquare, 
  User,
  Receipt,
  Briefcase,
  Shield,
  LifeBuoy,
  ChevronRight,
  Users as UsersIcon,
  Home,
  Grid
} from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarSeparator,
} from "@/components/ui/sidebar"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"

export function AppSidebar() {
  const location = useLocation()
  const { user, logout } = useWordPressAuth()
  const { theme, setTheme } = useTheme()

  // Debug: Log user data to see avatar URL (only in dev mode)
  const isDevMode = (window as any).WP_COCKPIT?.devMode || false
  if (isDevMode) {
    console.log('[AppSidebar] User data:', user)
    console.log('[AppSidebar] Avatar URL:', user?.avatarUrl)
  }

  // Get configuration from WordPress
  const wpConfig = typeof window !== 'undefined' ? (window as any).WP_COCKPIT : null
  
  // Get white label settings with fallbacks
  const whiteLabel = wpConfig?.whiteLabel || {}
  const logoUrl = whiteLabel.logo || (wpConfig?.assetsUrl 
    ? wpConfig.assetsUrl.replace('/assets/', '/') + 'logo.png'
    : '/logo.png')
  const cockpitTitle = whiteLabel.title || 'Cockpit'
  const cockpitSubtitle = whiteLabel.subtitle || 'Content Management'
  
  // Check feature permissions
  const features = wpConfig?.features || {}
  
  if (isDevMode) {
    console.log('[AppSidebar] Features:', features)
    console.log('[AppSidebar] project_management:', features.project_management)
  }
  
  const canAccessDashboard = features.dashboard === 'yes'
  const canAccessTracker = features.tracker === 'yes'
  const canAccessSettings = features.settings === 'yes'
  const canAccessProjects = features.projects === 'yes'
  const canAccessTasks = features.tasks === 'yes'
  const canAccessInvoices = features.invoices === 'yes'
  const canAccessProductionManagement = features.production_management === 'yes'
  const canAccessApplications = features.applications === 'yes'
  const canAccessJobs = features.jobs === 'yes'
  const canAccessInfluencerProfile = features.influencer_profile === 'yes'
  const canAccessSupport = features.support === 'yes'
  
  if (isDevMode) {
    console.log('[AppSidebar] canAccessProductionManagement:', canAccessProductionManagement)
  }
  
  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark")
  }

  // Core navigation items
  const coreNavItems = [
    ...(canAccessDashboard ? [{ path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' }] : []),
  ]

  // Work-related navigation items
  const workNavItems = [
    ...(canAccessTracker ? [{ path: '/tracker/timer', icon: Clock, label: 'Time Tracker' }] : []),
    ...(canAccessProjects ? [{ path: '/projects', icon: FolderIcon, label: 'Projects' }] : []),
    ...(canAccessTasks ? [{ path: '/tasks', icon: CheckSquare, label: 'Tasks' }] : []),
    ...(canAccessInvoices ? [{ path: '/invoices', icon: Receipt, label: 'Invoices' }] : []),
  ]

  // Careers navigation items
  const careersNavItems = [
    ...(canAccessApplications ? [{ path: '/applications', icon: Grid, label: 'My Applications' }] : []),
    ...(canAccessJobs ? [{ path: '/jobs', icon: Briefcase, label: 'My Job Listings' }] : []),
  ]

  // Production Management submenu items
  const projectManagementItems = canAccessProductionManagement ? [
    { path: '/production-management/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/production-management/projects', label: 'Projects', icon: FolderIcon },
    { path: '/production-management/timesheets', label: 'Timesheets', icon: Clock },
    { path: '/production-management/resources', label: 'Resources', icon: UsersIcon },
  ] : []

  // Content management navigation items
  const contentNavItems = [
    ...(canAccessInfluencerProfile ? [{ path: '/influencer-profile', icon: User, label: 'Influencer Profile' }] : []),
  ]

  // System navigation items
  const systemNavItems = [
    ...(canAccessSupport ? [{ path: '/support', icon: LifeBuoy, label: 'Support' }] : []),
    ...(canAccessSettings ? [{ path: '/settings', icon: Settings, label: 'Settings' }] : []),
  ]
  
  // Get WordPress homepage URL
  const wpHomeUrl = wpConfig?.homeUrl || wpConfig?.restApiUrl?.replace('/wp-json/', '/') || '/'
  
  // Check if user is administrator (ensure roles is an array)
  const userRoles = Array.isArray(user?.roles) ? user.roles : []
  const isAdmin = userRoles.includes('administrator')
  const adminUrl = wpConfig?.restApiUrl ? wpConfig.restApiUrl.replace('/wp-json/', '/wp-admin/') : '/wp-admin/'

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const handleSupportClick = (e: React.MouseEvent) => {
    const wpConfig = (window as any).WP_COCKPIT
    const fluentSupportActive = wpConfig?.fluentSupportActive ?? false
    const externalSupportUrl = wpConfig?.externalSupportUrl ?? 'https://kaizen.kainjoo.com/cockpit/support'
    
    if (!fluentSupportActive) {
      e.preventDefault()
      window.open(externalSupportUrl, '_blank')
    }
  }

  const renderNavGroup = (items: typeof coreNavItems, groupLabel?: string) => {
    if (items.length === 0) return null

    return (
      <SidebarGroup>
        {groupLabel && <SidebarGroupLabel>{groupLabel}</SidebarGroupLabel>}
        <SidebarGroupContent>
          <SidebarMenu>
            {items.map((item) => {
              const Icon = item.icon
              const active = isActive(item.path)
              const isSupport = item.path === '/support'
              return (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={active} 
                    tooltip={item.label}
                    onClick={isSupport ? handleSupportClick : undefined}>
                    <Link to={item.path}>
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    )
  }

  const renderProductionManagementSubmenu = () => {
    if (projectManagementItems.length === 0) return null
    
    const isProductionManagementActive = location.pathname.startsWith('/production-management')
    
    return (
      <SidebarMenuItem>
        <Collapsible defaultOpen={isProductionManagementActive} className="group/collapsible">
          <CollapsibleTrigger asChild>
            <SidebarMenuButton tooltip="Production Management">
              <Briefcase className="h-4 w-4" />
              <span>Production Management</span>
              <ChevronRight className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {projectManagementItems.map((item) => {
                const active = location.pathname === item.path
                const Icon = item.icon
                return (
                  <SidebarMenuSubItem key={item.path}>
                    <SidebarMenuSubButton asChild isActive={active}>
                      <Link to={item.path}>
                        {Icon && <Icon className="h-4 w-4" />}
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                )
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        </Collapsible>
      </SidebarMenuItem>
    )
  }

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="px-0">
        <Link to="/" className="flex items-center gap-3 px-2 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 hover:bg-sidebar-accent rounded-lg transition-colors">
          <img src={logoUrl} alt="Logo" className="h-8 w-8 rounded-full flex-shrink-0" />
          <div className="flex flex-col min-w-0 group-data-[collapsible=icon]:hidden">
            <span className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">{cockpitTitle}</span>
            <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{cockpitSubtitle}</span>
          </div>
        </Link>
      </SidebarHeader>

      <SidebarContent className="overflow-hidden">
        {/* Core features */}
        {renderNavGroup(coreNavItems)}

        {/* Work Management */}
        {(workNavItems.length > 0 || projectManagementItems.length > 0) && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupLabel>Work Management</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {workNavItems.map((item) => {
                    const Icon = item.icon
                    const active = isActive(item.path)
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
                          <Link to={item.path}>
                            <Icon className="h-4 w-4" />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
                  {renderProductionManagementSubmenu()}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {/* Content Management */}
        {contentNavItems.length > 0 && (
          <>
            <SidebarSeparator />
            {renderNavGroup(contentNavItems, "Content")}
          </>
        )}

        {/* Careers */}
        {careersNavItems.length > 0 && (
          <>
            <SidebarSeparator />
            {renderNavGroup(careersNavItems, "Careers")}
          </>
        )}

        {/* System */}
        {systemNavItems.length > 0 && (
          <>
            <SidebarSeparator />
            {renderNavGroup(systemNavItems, "System")}
          </>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {/* WordPress Homepage Link - visible to all users */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Website Home">
              <a href={wpHomeUrl} target="_blank" rel="noopener noreferrer">
                <Home className="h-4 w-4" />
                <span>Website Home</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          
          {/* WP Admin Link (for administrators only) */}
          {isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="WordPress Admin">
                <a href={adminUrl} target="_blank" rel="noopener noreferrer">
                  <Shield className="h-4 w-4" />
                  <span>WP Admin</span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          {/* User Profile */}
          <SidebarMenuItem>
            <SidebarMenuButton tooltip={user?.displayName || "User Profile"}>
              <UserAvatar 
                displayName={user?.displayName}
                avatarUrl={user?.avatarUrl}
                profilePicture={user?.profilePicture}
                className="h-4 w-4 flex-shrink-0"
              />
              <div className="flex flex-col min-w-0 overflow-hidden group-data-[collapsible=icon]:hidden">
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate whitespace-nowrap">
                  {user?.displayName}
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-400 truncate whitespace-nowrap">
                  {user?.email}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Theme Toggle */}
          <SidebarMenuItem>
            <SidebarMenuButton onClick={toggleTheme} tooltip={theme === "dark" ? "Light Mode" : "Dark Mode"}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Logout */}
          <SidebarMenuItem>
            <SidebarMenuButton onClick={logout} tooltip="Logout">
              <LogOut className="h-4 w-4" />
              <span>Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}